import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavigationBar from "../components/Navbar";
import Footer from "../components/Footer";
import PageSEO from "../components/PageSEO";
import ProductOfTheMonth from "../components/ProductOfTheMonth";
import CategoryCollections from "../components/CategoryCollections";
import NewCustomerDiscount from "../components/NewCustomerDiscount";
import ProductRecommendations from "../components/ProductRecommendations";
import NewsletterSignup from "../components/NewsletterSignup";
import { Container, Row, Col, Button } from "react-bootstrap";
import {
  FaTruck,
  FaTools,
  FaShieldAlt,
  FaClock,
  FaCheckCircle,
  FaArrowRight,
  FaHeadset,
  FaSquare,
  FaRulerCombined,
  FaSyncAlt,
  FaIndustry,
} from "react-icons/fa";
import "./HomeConcept3.css";
import "../styles/concept3-theme.css";
import { getImageUrl, API_URL, getPlaceholderImage } from '../config';
// Hero image served from uploads directory

function HomeConcept3() {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [productOfTheMonth, setProductOfTheMonth] = useState(null);
  const [monthlyVariantId, setMonthlyVariantId] = useState(null);
  const [monthlyDiscount, setMonthlyDiscount] = useState(20);
  const [loading, setLoading] = useState(true);

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "TR Ventilation",
    "description": "Professional HVAC & Ventilation Supplies",
    "url": "https://trventilation.com",
    "telephone": "+441482205358",
    "email": "sales@trventilation.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Technology Park, 5 Hamburg Rd",
      "addressLocality": "Kingston upon Hull",
      "addressRegion": "East Yorkshire",
      "postalCode": "HU7 0WD",
      "addressCountry": "GB"
    },
    "areaServed": ["UK", "Yorkshire", "Hull"],
    "priceRange": "£",
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch featured products
        const productsResponse = await fetch(`${API_URL}/products?status=active&limit=6`);
        if (productsResponse.ok) {
          const data = await productsResponse.json();
          setFeaturedProducts(data.products || []);
        }

        // Fetch product of the month settings
        const settingsResponse = await fetch(`${API_URL}/site-settings`);
        if (settingsResponse.ok) {
          const settings = await settingsResponse.json();
          if (settings.productOfTheMonth?.productId) {
            // Fetch the full product details
            const productResponse = await fetch(
              `${API_URL}/products/${settings.productOfTheMonth.productId}`
            );
            if (productResponse.ok) {
              const productData = await productResponse.json();
              setProductOfTheMonth(productData);
              setMonthlyVariantId(settings.productOfTheMonth.variantId || null);
              setMonthlyDiscount(settings.productOfTheMonth.discountPercentage || 20);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  const features = [
    {
      icon: FaTruck,
      title: "Fast Delivery",
      description: "1-2 day delivery across the UK on in-stock items",
    },
    {
      icon: FaShieldAlt,
      title: "Quality Guaranteed",
      description: "All products backed by comprehensive manufacturer warranties",
    },
    {
      icon: FaTools,
      title: "Expert Support",
      description: "Technical assistance from certified HVAC professionals",
    },
    {
      icon: FaHeadset,
      title: "Dedicated Support",
      description: "Call us for technical advice and custom quotes",
    },
  ];

  const stats = [
    { number: "15+", label: "Years Experience" },
    { number: "300+", label: "Products Available" },
    { number: "5000+", label: "Happy Customers" },
    { number: "48hrs", label: "UK Delivery" },
  ];

  return (
    <>
      <PageSEO
        title="Commercial HVAC & Ductwork Supplies Hull | Industrial Ventilation UK"
        description="Premium commercial HVAC supplies and industrial ventilation equipment. Buy ductwork, fans, extraction systems online with next-day UK delivery."
        keywords="commercial HVAC supplies, ductwork supplies Yorkshire, industrial ventilation systems"
        structured={organizationSchema}
      />
      <NavigationBar />

      {/* Hero Section - Concept 3 with Glassmorphism */}
      <section className="hero-section-concept-3" style={{ backgroundImage: `url(${getImageUrl('/uploads/products/home-hero.jpg')})` }}>
        <Container>
          <Row>
            <Col lg={6}>
              <div className="hero-content-concept-3">
                <div>
                  <h1>Premium HVAC & Ventilation Solutions</h1>
                  <p>Your trusted partner for industrial ventilation supplies in Hull and Yorkshire. Quality products, competitive prices, and exceptional service you can rely on.</p>
                  <div className="hero-actions-concept-3">
                    <Button size="lg" className="btn-primary me-3" onClick={() => navigate("/products")}>
                      Browse Products <FaArrowRight className="ms-2" />
                    </Button>
                    <Button size="lg" variant="outline-light" onClick={() => navigate("/contact")}>
                      Get a Quote
                    </Button>
                  </div>
                  <div className="trust-pills">
                    <div className="trust-pill">
                      <FaCheckCircle />
                      Trade Accounts Save 15%+
                    </div>
                    <div className="trust-pill">
                      <FaCheckCircle />
                      Next-Day UK Delivery
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="stats-section-concept-3">
        <Container>
          <div className="stats-grid-concept-3">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card-concept-3">
                <div className="stat-number-concept-3">{stat.number}</div>
                <div className="stat-label-concept-3">{stat.label}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section-concept-3">
        <Container>
          <div className="section-header-concept-3">
            <div className="section-divider-concept-3"></div>
            <h2 className="section-title-concept-3">Why Choose Us</h2>
            <p className="section-subtitle-concept-3">
              We deliver excellence across every aspect of our service
            </p>
          </div>

          <Row>
            {features.map((feature, index) => (
              <Col lg={3} md={6} key={index} className="mb-4">
                <div className="feature-card-concept-3">
                  <div className="feature-icon-wrapper">
                    <feature.icon className="feature-icon-concept-3" />
                  </div>
                  <h5 className="feature-title-concept-3">{feature.title}</h5>
                  <p className="feature-text-concept-3">{feature.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Bespoke Manufacturing Section */}
      <section className="bespoke-manufacturing-section-concept-3">
        <Container>
          <div className="section-header-concept-3">
            <div className="section-divider-concept-3"></div>
            <h2 className="section-title-concept-3">Bespoke Manufacturing Services</h2>
            <p className="section-subtitle-concept-3">
              Custom sheet metal fabrication tailored to your exact specifications
            </p>
          </div>

          <div className="manufacturing-services-list">
            <Row>
              <Col lg={6} className="mb-5">
                <div className="manufacturing-item-concept-3">
                  <div className="manufacturing-item-icon-wrapper">
                    <FaSquare className="manufacturing-item-icon" />
                  </div>
                  <div className="manufacturing-item-content">
                    <h4>Square Ductwork</h4>
                    <p>
                      Precision-engineered square ducts built to your exact specifications for optimal airflow.
                    </p>
                  </div>
                </div>
              </Col>
              <Col lg={6} className="mb-5">
                <div className="manufacturing-item-concept-3">
                  <div className="manufacturing-item-icon-wrapper">
                    <FaRulerCombined className="manufacturing-item-icon" />
                  </div>
                  <div className="manufacturing-item-content">
                    <h4>Rectangular Ductwork</h4>
                    <p>
                      Custom rectangular ductwork designed for efficient ventilation in any space.
                    </p>
                  </div>
                </div>
              </Col>
              <Col lg={6} className="mb-5">
                <div className="manufacturing-item-concept-3">
                  <div className="manufacturing-item-icon-wrapper">
                    <FaSyncAlt className="manufacturing-item-icon" />
                  </div>
                  <div className="manufacturing-item-content">
                    <h4>Square-to-Round Grille Boxes</h4>
                    <p>
                      Smooth transition fittings manufactured to exact standards for professional installation.
                    </p>
                  </div>
                </div>
              </Col>
              <Col lg={6} className="mb-5">
                <div className="manufacturing-item-concept-3">
                  <div className="manufacturing-item-icon-wrapper">
                    <FaIndustry className="manufacturing-item-icon" />
                  </div>
                  <div className="manufacturing-item-content">
                    <h4>General Sheet Metal Work</h4>
                    <p>
                      Bespoke metal fabrication services tailored to your commercial and industrial needs.
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          <div className="manufacturing-cta-concept-3">
            <p style={{ fontSize: '1.05rem', marginBottom: '1.5rem', color: '#666' }}>
              All items are manufactured to your exact specifications, built to the highest standards, and suitable for commercial and industrial ventilation systems.
            </p>
            <Button
              className="btn-primary"
              size="lg"
              onClick={() => navigate("/contact")}
            >
              Discuss Your Project <FaArrowRight className="ms-2" />
            </Button>
          </div>
        </Container>
      </section>

      {/* New Customer Discount Section */}
      <section className="new-customer-section-concept-3">
        <Container>
          <NewCustomerDiscount />
        </Container>
      </section>

      {/* Product of the Month Section */}
      {productOfTheMonth && (
        <section className="product-of-month-section-concept-3">
          <Container>
            <ProductOfTheMonth product={productOfTheMonth} discount={monthlyDiscount} variantId={monthlyVariantId} />
          </Container>
        </section>
      )}

      {/* Category Collections Section */}
      <CategoryCollections />

      {/* Featured Products Section */}
      <section className="products-section-concept-3">
        <Container>
          <div className="section-header-concept-3">
            <div className="section-divider-concept-3"></div>
            <h2 className="section-title-concept-3">Featured Products</h2>
            <p className="section-subtitle-concept-3">
              Explore our range of premium ventilation and ducting solutions
            </p>
          </div>

          {loading ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Row>
              {featuredProducts.slice(0, 6).map((product) => (
                <Col lg={4} md={6} key={product._id} className="mb-4">
                  <div className="product-card-concept-3">
                    <div className="product-img-wrapper-concept-3">
                      <img
                        src={getImageUrl(product.image || product.images?.[0])}
                        alt={product.name}
                        onError={(e) => {
                          e.target.src = getPlaceholderImage('No Image');
                        }}
                      />
                      {product.inStock && (
                        <span className="product-badge-concept-3">In Stock</span>
                      )}
                    </div>

                    <div className="product-info-concept-3">
                      <h4 className="product-name-concept-3">{product.name}</h4>
                      <p className="product-desc-concept-3">
                        {product.shortDescription || product.description?.substring(0, 80)}...
                      </p>

                      <div className="product-footer-concept-3">
                        <span className="product-price-concept-3">
                          £{Number(product.price).toFixed(2)}
                        </span>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate(`/product/${product._id}`)}
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          <div className="text-center mt-5">
            <Button
              size="lg"
              className="btn-primary"
              onClick={() => navigate("/products")}
            >
              View All Products
              <FaArrowRight className="ms-2" />
            </Button>
          </div>
        </Container>
      </section>

      {/* Trending Products Section */}
      <section className="trending-section-concept-3">
        <Container>
          <ProductRecommendations type="trending" />
        </Container>
      </section>

      {/* Why Choose TR Ventilation Section */}
      <section className="why-choose-section-concept-3">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="section-header-concept-3">
                <div className="section-divider-concept-3"></div>
                <h2 className="section-title-concept-3">Why Choose TR Ventilation?</h2>
                <p className="section-subtitle-concept-3">
                  15+ years of industry expertise delivering premium HVAC solutions
                </p>
              </div>

              <div className="benefits-list-concept-3">
                <div className="benefit-item-concept-3">
                  <FaCheckCircle className="benefit-icon-concept-3" />
                  <div>
                    <h4>Extensive Inventory</h4>
                    <p>Full range of ducting, fans, and accessories in stock</p>
                  </div>
                </div>

                <div className="benefit-item-concept-3">
                  <FaCheckCircle className="benefit-icon-concept-3" />
                  <div>
                    <h4>Competitive Pricing</h4>
                    <p>Trade discounts and bulk order rates available</p>
                  </div>
                </div>

                <div className="benefit-item-concept-3">
                  <FaCheckCircle className="benefit-icon-concept-3" />
                  <div>
                    <h4>Expert Support</h4>
                    <p>Certified HVAC professionals ready to help</p>
                  </div>
                </div>

                <div className="benefit-item-concept-3">
                  <FaCheckCircle className="benefit-icon-concept-3" />
                  <div>
                    <h4>Fast Delivery</h4>
                    <p>Next-day UK delivery on in-stock items</p>
                  </div>
                </div>

                <div className="benefit-item-concept-3">
                  <FaCheckCircle className="benefit-icon-concept-3" />
                  <div>
                    <h4>Quality Guaranteed</h4>
                    <p>All products backed by manufacturer warranties</p>
                  </div>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <div className="why-choose-content-concept-3">
                <div className="info-box-concept-3">
                  <h3>Trusted by HVAC Professionals</h3>
                  <p>
                    Since 2023, TR Ventilation has been the preferred supplier for contractors, builders, and
                    facility managers across Hull and Yorkshire. We understand the critical importance of quality
                    ventilation systems and professional support in commercial and industrial environments.
                  </p>
                  <p style={{ marginBottom: '1.5rem' }}>
                    As specialists in commercial HVAC supplies, ductwork systems, and industrial ventilation,
                    we provide comprehensive solutions tailored to your needs.
                  </p>
                  <Button
                    className="btn-primary"
                    size="lg"
                    onClick={() => navigate("/about")}
                  >
                    Learn More About Us
                    <FaArrowRight className="ms-2" />
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section-concept-3">
        <Container>
          <div className="cta-content-concept-3">
            <h2 className="cta-title-concept-3">
              Ready to Find Your Perfect Solution?
            </h2>
            <p className="cta-subtitle-concept-3">
              Browse our complete range of ventilation products or contact our team for a custom quote tailored to your needs.
            </p>

            <div className="cta-actions-concept-3">
              <Button
                className="btn-primary"
                size="lg"
                onClick={() => navigate("/products")}
              >
                Browse Catalog
              </Button>
              <Button
                className="btn-secondary"
                size="lg"
                onClick={() => navigate("/contact")}
              >
                Contact Us
              </Button>
            </div>
          </div>
        </Container>
      </section>

      <Footer />
    </>
  );
}

export default HomeConcept3;
